import { useEffect, useState } from "react";
import Loader from "../loader/loader";
import AppLayout from "../layout/AppLayout";
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import Cookies from "js-cookie";
import regenerateToken from "../regenerateToken";
import axios from "axios";
import { Accordion, Spinner } from "react-bootstrap";
import { MdDragIndicator } from "react-icons/md";
import { FaSave, FaSyncAlt, FaSearch } from "react-icons/fa";
import { useAlert } from "react-alert";
import './parameterOrdering.css';
import { buildRequestConfig } from '../../utils/apiHelper';
import { isAdmin as checkAdmin } from '../../utils/authUtils';

// --- Subcomponents Defined OUTSIDE to prevent re-renders ---

const SortableRow = ({ id, children }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    cursor: "grab",
    zIndex: isDragging ? 999 : "auto",
    position: "relative",
    opacity: isDragging ? 0.8 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      {children}
    </div>
  );
};

const ProfileBlock = ({ profile, activeAccordionKeys, handleAccordionToggle }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: profile.profileId });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // We do NOT want the dragging of the profile to be triggered by clicking anywhere on the item
    // because we have internal interactive elements (Accordion).
    // The handle will have the listeners.
    marginBottom: '1rem',
    zIndex: isDragging ? 999 : "auto",
    position: "relative",
    opacity: isDragging ? 0.9 : 1,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <div className="po-profile-card">
        <Accordion
          activeKey={activeAccordionKeys}
          onSelect={() => handleAccordionToggle(profile.profileId)}
          flush
          alwaysOpen
        >
          <Accordion.Item eventKey={profile.profileId} style={{ border: 'none' }}>
            <Accordion.Header className="po-accordion-header-wrapper">
              <div className="po-profile-header-content">
                {/* DRAG HANDLE for Profile Reordering */}
                <div
                  {...attributes}
                  {...listeners}
                  className="po-drag-handle"
                  onClick={(e) => e.stopPropagation()} // Prevent accordion toggle when dragging
                  title="Drag to reorder profile"
                >
                  <MdDragIndicator size={20} />
                </div>
                <div className="po-profile-title">
                  {profile.profileTitle}
                </div>
                <div className="po-param-count">
                  {profile.parameters.length} parameters
                </div>
              </div>
            </Accordion.Header>

            <Accordion.Body className="po-accordion-body">
              <SortableContext
                items={profile.parameters.map((p) => p.clientParameterId)}
                strategy={verticalListSortingStrategy}
              >
                {profile.parameters.map((param) => (
                  <SortableRow
                    key={param.clientParameterId}
                    id={param.clientParameterId}
                  >
                    <div className="po-param-row">
                      <div className="po-param-handle">
                        <MdDragIndicator size={18} />
                      </div>
                      <div className="po-param-id">
                        {param.clientParameterId}
                      </div>
                      <div className="po-param-name">
                        {param.ngParameterName}
                      </div>
                    </div>
                  </SortableRow>
                ))}
              </SortableContext>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

// --- Main Component ---

const ParameterOrdering = () => {
  const alert = useAlert();
  const [loading, setLoading] = useState(false);
  const [existingMapping, setExistingMapping] = useState([]);
  const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
  const [postLoader, setPostLoader] = useState(false);
  const [callStateConfigApi, setCallStateConfigApi] = useState(false);
  const [searchParam, setSearchParam] = useState("");

  const convertMappingToArray = (parameterMappping, profileMapping) => {
    let output = [];
    if (!parameterMappping || !profileMapping) {
      return output;
    }
    for (const key in parameterMappping) {
      const specificProfile = profileMapping[parameterMappping[key]];

      const existingProfile = output.findIndex(
        (each) => each.profileId === specificProfile
      );
      let paramObj = {
        clientParameterId: key,
        ngParameterName: parameterMappping[key],
      };
      if (specificProfile) {
        if (existingProfile === -1) {
          const obj = {
            profileId: specificProfile,
            profileTitle: specificProfile,
            parameters: [],
          };
          obj.parameters.push(paramObj);
          output.push(obj);
        } else {
          let existingParmeter = output[existingProfile].parameters.find(
            (each) => each.clientParameterId === key
          );
          if (!existingParmeter) {
            output[existingProfile].parameters.push(paramObj);
          }
        }
      }
    }

    return output;
  };

  const getExistingMapping = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      const [parameterRes, profileRes] = await Promise.all([
        axios(buildRequestConfig({ method: "GET", url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter" })),
        axios(buildRequestConfig({ method: "GET", url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile" })),
      ]);
      return { parameterRes, profileRes };
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const parameterConfig = {
      headers: {
        authorization: idToken,
      },
      method: "GET",
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/parameter",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const profileConfig = {
      headers: {
        authorization: idToken,
      },
      method: "GET",
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/mapping/profile",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const [parameterRes, profileRes] = await Promise.all([
      axios(parameterConfig),
      axios(profileConfig),
    ]);
    return { parameterRes, profileRes };
  };

  const handleAccordionToggle = (eventKey) => {
    setActiveAccordionKeys((prevKeys) => {
      // Logic for multi-select accordion
      if (prevKeys.includes(eventKey)) {
        return prevKeys.filter((key) => key !== eventKey);
      } else {
        return [...prevKeys, eventKey];
      }
    });
  };

  useEffect(() => {
    getFinalOrderData();
  }, []);

  useEffect(() => {
    if (callStateConfigApi) {
      (async function RetrieveExistingMappingData() {
        setLoading(true);
        const apiResponse = await getExistingMapping();
        const statusCodeParam = apiResponse?.parameterRes?.status;
        const statusCodeProfile = apiResponse?.profileRes?.status;
        if (statusCodeParam === 401 || statusCodeProfile === 401) {
          await regenerateToken();
          const apiResponse = await getExistingMapping();
          if (
            apiResponse?.parameterRes?.data?.isSuccess &&
            apiResponse?.profileRes?.data?.isSuccess
          ) {
            const modifiedMapping = convertMappingToArray(
              apiResponse.parameterRes.data?.data?.idMapping,
              apiResponse.profileRes.data?.data?.profileMapping
            );
            setExistingMapping(modifiedMapping);
          }
        } else {
          if (
            apiResponse?.parameterRes?.data?.isSuccess &&
            apiResponse?.profileRes?.data?.isSuccess
          ) {
            const modifiedMapping = convertMappingToArray(
              apiResponse.parameterRes.data?.data?.idMapping,
              apiResponse.profileRes.data?.data?.profileMapping
            );
            setExistingMapping(modifiedMapping);
          }
        }
        setLoading(false);
      })();
    }
  }, [callStateConfigApi]);

  const getFormattedPayload = () => {
    const profileOrder = [];
    const parameterOrder = [];

    if (existingMapping?.length) {
      existingMapping.forEach((profile) => {
        if (profile?.profileTitle) {
          profileOrder.push(profile.profileTitle);
        }

        let parameterArray = profile.parameters;
        if (parameterArray && parameterArray?.length) {
          parameterArray.forEach((eachParam) => {
            if (eachParam?.ngParameterName) {
              parameterOrder.push(eachParam.ngParameterName);
            }
          });
        }
      });
    }

    return { profileOrder, parameterOrder, groupedOrder: existingMapping };
  };

  const getOrderData = async () => {
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "GET",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/order",
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const config = {
      headers: {
        authorization: idToken,
      },
      method: "GET",
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/order",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
    };
    const response = await axios(config);
    return response;
  };

  const getFinalOrderData = async () => {
    setLoading(true);
    setCallStateConfigApi(false);
    const apiResponse = await getOrderData();
    const statusCode = apiResponse?.status;

    if (statusCode === 401) {
      await regenerateToken();
      const apiResponse = await getOrderData();
      if (
        apiResponse?.data?.isSuccess &&
        apiResponse?.data?.data?.parameterOrder?.groupedOrder
      ) {
        setExistingMapping(apiResponse.data.data.parameterOrder.groupedOrder);
      } else {
        setCallStateConfigApi(true);
      }
    } else {
      if (
        apiResponse?.data?.isSuccess &&
        apiResponse?.data?.data?.parameterOrder?.groupedOrder
      ) {
        setExistingMapping(apiResponse.data.data.parameterOrder.groupedOrder);
      } else {
        setCallStateConfigApi(true);
      }
    }
    setLoading(false);
  };

  const postOrderData = async () => {
    const payload = getFormattedPayload();
    // ── ADMIN PATH: use admin token + inject clientId if a client is selected
    if (checkAdmin()) {
      return await axios(buildRequestConfig({
        method: "POST",
        url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/order",
        data: payload,
      }));
    }
    // ── CLIENT PATH (unchanged) ──────────────────────────────────────────
    const idToken = Cookies.get("idToken");
    const config = {
      headers: {
        authorization: idToken,
        "Content-Type": "application/json",
      },
      method: "POST",
      url: "https://ap94204xve.execute-api.ap-south-1.amazonaws.com/prod/config/order",
      validateStatus: function (status) {
        return status >= 200 && status <= 401;
      },
      data: JSON.stringify(payload),
    };
    const response = await axios(config);
    return response;
  };

  const postFinalOrderData = async () => {
    setPostLoader(true);
    const apiResponse = await postOrderData();
    const statusCode = apiResponse?.status;

    if (statusCode === 401) {
      await regenerateToken();
      const apiResponse = await postOrderData();
      if (apiResponse?.data?.isSuccess) {
        alert.success("Profile & Parameter Order updated successfully");
      }
    } else {
      if (apiResponse?.data?.isSuccess) {
        alert.success("Profile & Parameter Order updated successfully");
      }
    }
    setPostLoader(false);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const isProfile = existingMapping.some((p) => p.profileId === active.id);
    if (isProfile) {
      const oldIndex = existingMapping.findIndex(
        (p) => p.profileId === active.id
      );
      const newIndex = existingMapping.findIndex(
        (p) => p.profileId === over.id
      );
      const reordered = arrayMove(existingMapping, oldIndex, newIndex);
      setExistingMapping(reordered);
    } else {
      const updated = existingMapping.map((profile) => {
        const paramIndex = profile.parameters.findIndex(
          (p) => p.clientParameterId === active.id
        );

        if (paramIndex === -1) return profile;

        const overIndex = profile.parameters.findIndex(
          (p) => p.clientParameterId === over.id
        );

        if (overIndex === -1) return profile;

        const reorderedParams = arrayMove(
          profile.parameters,
          paramIndex,
          overIndex
        );

        return {
          ...profile,
          parameters: reorderedParams,
        };
      });

      setExistingMapping(updated);
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor)
  );

  const filteredParams = searchParam
    ? existingMapping.filter((profile) =>
      profile.parameters.some((param) =>
        (param.ngParameterName || "")
          .toLowerCase()
          .includes(searchParam.toLowerCase())
      )
    )
    : existingMapping;

  return (
    <AppLayout>
      {loading ? (
        <Loader />
      ) : (
        <div className="po-container">
          {/* Header */}
          <header className="po-header">
            <div className="po-title-group">
              <h1 className="po-title">Parameters Ordering</h1>
              <p className="po-subtitle">Drag and drop to reorder profiles and parameters for reports.</p>
            </div>
            <div className="po-actions-top">
              <button
                className="po-btn-secondary"
                onClick={() => setCallStateConfigApi(true)}
                title="Reload original mapping from server"
              >
                <FaSyncAlt /> Refetch Data
              </button>
              <button
                className="po-btn-primary"
                onClick={postFinalOrderData}
                disabled={postLoader}
              >
                {postLoader ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  <><FaSave /> Confirm Changes</>
                )}
              </button>
            </div>
          </header>

          {/* Search */}
          <div className="po-search-container">
            <FaSearch className="po-search-icon" />
            <input
              className="po-search-input"
              placeholder="Search parameters by name..."
              value={searchParam}
              onChange={(event) => setSearchParam(event.target.value)}
            />
          </div>

          {/* Content List */}
          <div className="po-content">
            {filteredParams?.length ? (
              <DndContext
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
                sensors={sensors}
              >
                <SortableContext
                  items={filteredParams.map((item) => item.profileId)}
                  strategy={verticalListSortingStrategy}
                >
                  {filteredParams.map((profile) => (
                    <ProfileBlock
                      profile={profile}
                      key={profile.profileId}
                      activeAccordionKeys={activeAccordionKeys}
                      handleAccordionToggle={handleAccordionToggle}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-5 text-muted">
                No parameters found matching your search.
              </div>
            )}
          </div>
        </div>
      )}
    </AppLayout>
  );
};
export default ParameterOrdering;
